import { CommonModule } from '@angular/common';
import { Component, computed, inject, Type } from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonCardV2Component } from '../../lesson-card-v2/lesson-card-v2.component';
import { LessonDetailDailyGradesComponent } from '../lesson-detail-daily-grades/lesson-detail-daily-grades.component';
import { LessonDetailObjectivesComponent } from '../lesson-detail-objectives/lesson-detail-objectives.component';
import { LessonDetailProjectProgressComponent } from '../lesson-detail-project-progress/lesson-detail-project-progress.component';
import { LessonDetailHomeworkComponent } from '../lesson-detail-homework/lesson-detail-homework.component';
import { LessonDetailSummaryComponent } from '../lesson-detail-summary/lesson-detail-summary.component';
import { LessonDetailFormStateService, IAttendanceSaveParams } from '../lesson-detail-form-state.service';
import { LessonDetailSurveyComponent } from '../lesson-detail-surveys/lesson-detail-survey.component';
import { LessonDetailProjectFinalComponent } from '../lesson-detail-project-final/lesson-detail-project-final.component';
import { LessonDetailUnavailableComponent } from '../lesson-detail-unavailable/lesson-detail-unavailable.component';
import { SaveReviewDialogComponent, ISaveReviewGradeItem, ISaveReviewResult } from '../save-review-dialog/save-review-dialog.component';
import {
	SaveFinalReviewDialogComponent,
	ISaveFinalProjectCriteriaItem,
	ISaveFinalReviewResult
} from '../save-final-review-dialog/save-final-review-dialog.component';
import {
	MDContentBoxComponent,
	MDDialogService,
	MDButtonGroupComponent,
	MDButtonToggleComponent,
	MDDropDownComponent,
	MDToolTipDirective,
	MDAlertService,
	IAlert
} from '@melodic-ui/components';
import { TimezoneAwareDatePipe } from '@lb/pipes';
import { AnalyticsEvent, AnalyticsService, LessonMetadataService } from '@lb/services';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces';
import { IndicatorType } from '@melodic-ui/types';
import { catchError, delay, filter, take } from 'rxjs';
import { AttendanceService } from '@lb/api/mylb/services';
import { LessonService } from '@lb/api/mylb/services/lesson.service';
import { LessonApiService } from '@lb/api/lesson/services/lesson-api.service';
import { ConfirmationDialogComponent } from '@lb/components/confirmation-dialog/confirmation-dialog.component';
import { RescheduleLessonDialogService } from '@lb/services/reschedule-lesson-dialog.service';
import { canRescheduleLesson } from '@lb/components/lessons/utils/lesson-helper.util';
import { LessonRosterAttendanceComponent } from '../lesson-roster-attendance/lesson-roster-attendance.component';
import { signal } from '@angular/core';

@Component({
	selector: 'lb-instructor-lesson-detail',
	templateUrl: './instructor-lesson-detail.component.html',
	styleUrl: './instructor-lesson-detail.component.scss',
	imports: [
		CommonModule,
		LessonDetailDailyGradesComponent,
		LessonDetailObjectivesComponent,
		LessonDetailProjectProgressComponent,
		LessonDetailHomeworkComponent,
		LessonDetailSummaryComponent,
		LessonCardV2Component,
		MDDropDownComponent,
		LessonDetailSurveyComponent,
		LessonDetailProjectFinalComponent,
		LessonDetailUnavailableComponent,
		LessonRosterAttendanceComponent,
		MDContentBoxComponent,
		MDButtonGroupComponent,
		MDButtonToggleComponent,
		MDToolTipDirective
	],
	standalone: true
})
export class InstructorLessonDetailComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);
	private readonly dialogService = inject(MDDialogService);
	private readonly alertService = inject(MDAlertService);
	private readonly lessonService = inject(LessonService);
	private readonly lessonApiService = inject(LessonApiService);
	private readonly lessonMetadataService = inject(LessonMetadataService);
	private readonly attendanceService = inject(AttendanceService);
	private readonly _rescheduleLessonDialogService = inject(RescheduleLessonDialogService);
	private readonly _analytics = inject(AnalyticsService);
	private readonly _datePipe = inject(TimezoneAwareDatePipe);

	private readonly _now = new Date();
	private _updatingLessonType = false;
	private _initializingTypeDropdown = true;

	lesson = this.state.lessonRequired;
	formDirty = this.formState.isDirty;
	canTakeAttendanceForLesson = this.formState.canTakeAttendanceForLesson;
	fieldsDisabled = this.formState.fieldsDisabled;
	showSaveDraft = this.formState.showSaveDraft;
	canSaveDraft = this.formState.canSaveDraft;

	/**
	 * The attendance roster's own gate. The detail fields open before the lesson starts (so drafts can
	 * be pre-populated), but attendance does not — it stays on the original start-time rule, so the
	 * roster cannot bind to fieldsDisabled any more.
	 */
	attendanceDisabled = computed(() => !this.canTakeAttendanceForLesson());

	// --- Multi-attendance (chirp room) roster (T14) ---
	// The roster replaces the single attendance value: instructors check who is present, and the save
	// flow routes through the roster endpoint (server derives absents + session value, D6/D11).
	isMultiAttendance = computed(() => this.lesson().isMultiAttendance);
	rosterMembers = computed(() => this.lesson().members ?? []);
	rosterPresentIDs = signal<number[]>([]);
	rosterNoAttendees = signal<boolean>(false);
	canSave = this.formState.canSave;
	isValid = this.formState.isValid;
	isLocked = this.formState.isLocked;
	isReadOnly = this.formState.isReadOnly;
	editWindowOpensAt = this.formState.editWindowOpensAt;
	isInstructional = this.state.isInstructional;
	isFinalSemester = this.state.isFinalSemester;
	hasGradingTemplate = this.state.hasGradingTemplate;
	isSaving = this.state.isSaving;
	showDetails = this.state.showDetails;

	pendingReschedule = computed(() => this.lesson().lessonStatusID === 3);
	awaitingPayment = computed(() => this.lesson().lessonStatusID === 4);
	lessonTypeOptions = computed(() => this.lessonMetadataService.getLessonTypeOptions(this.lesson().programID)());
	hasTakenAttendance = computed(() => this.lesson().attendanceStatusID != null);
	isComplete = computed(() => this.lesson().lessonStatusID === 5);

	canJoin = computed(() => !this.pendingReschedule() && !this.awaitingPayment() && !this.isComplete() && !this.hasTakenAttendance());

	// Completing a lesson is allowed from the moment it starts — instructors write the lesson up while
	// teaching it, so waiting for the scheduled end time was too late. Already-complete lessons
	// (status 5) render the button as "Save" and stay saveable regardless of time; canCompleteLesson
	// covers both cases. Note this is NOT the same gate as editing the detail fields, which is open
	// before the lesson starts so drafts can be pre-populated.
	canComplete = this.formState.canCompleteLesson;

	/**
	 * A multi-attendance lesson cannot be completed until the instructor has reviewed the roster:
	 * either someone is checked present, or "No attendees" is explicitly confirmed. Single-attendance
	 * lessons (and lessons whose attendance is already recorded) are unaffected.
	 */
	attendanceSelectionMade = computed(() => {
		if (!this.isMultiAttendance() || !this.canTakeAttendanceForLesson() || this.rosterMembers().length === 0) {
			return true;
		}

		return this.rosterPresentIDs().length > 0 || this.rosterNoAttendees();
	});

	/**
	 * Why the Complete button is disabled while the lesson's edit window is still closed. Empty when
	 * the button is live, or when it is disabled for another reason (finalized semester, nothing to
	 * save) that this notice would misexplain — an empty string leaves the tooltip off.
	 */
	completeTooltip = computed(() => {
		if (this.isReadOnly()) {
			return '';
		}

		if (this.canComplete()) {
			// Edit window is open but the roster hasn't been reviewed yet — that's the only
			// selection-based block, so explain it instead of leaving a dead button.
			return this.attendanceSelectionMade() ? '' : 'Take attendance first — check who attended, or select "No attendees"';
		}

		const opensAt = this.editWindowOpensAt();
		if (!opensAt) {
			return '';
		}

		return `Lesson can be completed after ${this._datePipe.transform(opensAt, "MMM d 'at' h:mm a")}`;
	});

	/**
	 * What the draft button does, said plainly — the point instructors need to trust is that drafting
	 * does not quietly mark the lesson taught.
	 *
	 * Also carries the "completable from" time. Draft replaces the Complete button entirely while the
	 * lesson has not started, so this is the only place left that answers "when can I complete this?".
	 * Shown whether or not the button is currently live: explaining what it does is just as useful
	 * when it is greyed out because nothing has been entered yet.
	 */
	draftTooltip = computed(() => {
		if (!this.showSaveDraft()) {
			return '';
		}

		const opensAt = this.editWindowOpensAt();
		if (!opensAt) {
			return 'Saves lesson details only. Attendance is recorded when you complete the lesson.';
		}

		return `Saves lesson details only. Attendance is recorded when you complete the lesson, from ${this._datePipe.transform(
			opensAt,
			"MMM d 'at' h:mm a"
		)}.`;
	});

	typeDropdownReady = computed(() => {
		const programID = this.lesson().programID;
		const typesReady = this.lessonMetadataService.typesLoadedForProgram(programID);
		return typesReady && this.lessonTypeOptions().length > 0;
	});

	lessonTypeDisabled = computed(
		() =>
			this.state.loading() ||
			this._updatingLessonType ||
			this.lesson().lessonStatusID === 3 ||
			this.lesson().lessonStatusID === 4 ||
			this.lesson().lessonStatusID === 5
	);

	constructor() {
		toObservable(this.lesson)
			.pipe(
				filter((lesson) => !!lesson && !this.lessonMetadataService.isLoadedForProgram(lesson.programID)()),
				takeUntilDestroyed()
			)
			.subscribe((lesson) => {
				this.lessonMetadataService.initializeForProgram(lesson.programID);
			});

		toObservable(this.typeDropdownReady)
			.pipe(
				filter((ready) => ready && this._initializingTypeDropdown),
				delay(50),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				this._initializingTypeDropdown = false;
			});
	}

	// Instructional lessons only get the full graded layout when a grading template is actually
	// assigned to the program AND that template is graded (chirp room templates are not). Without
	// either, they fall back to the same summary-only view as any other non-graded lesson type.
	isGradedInstructional = computed(() => this.isInstructional() && this.hasGradingTemplate() && this.lesson().isGraded);

	summaryOnly = computed(
		() =>
			!this.isGradedInstructional() &&
			!this.isFinalSemester() &&
			// A multi-attendance (chirp room) lesson always has content for the left column: the
			// roll-call roster. Without this, room lessons (LessonTypeID = ChirpRoom, never
			// instructional) collapsed to the summary-only layout and attendance had no home.
			!this.isMultiAttendance() &&
			this.state.activeLearnerSurveys().length === 0 &&
			this.state.activeInstructorSurveys().length === 0
	);

	// Server-computed over the whole enrollment, so a replacement instructor sees the semester's true
	// ordinal rather than a count that restarts with them (ILessonListItem.lessonNumber).
	lessonNumber = computed(() => this.lesson().lessonNumber ?? null);

	saveButtonText = computed(() => {
		if (this.isSaving()) {
			return 'Saving...';
		}

		// Lesson is already complete, we can update data still
		if (this.lesson().lessonStatusID === 5) {
			return 'Save';
		}

		return 'Complete';
	});

	canReschedule = computed(() => canRescheduleLesson(this.state.experience(), this.lesson(), this._now));

	updateLessonType(type: unknown): void {
		const lesson = this.lesson();

		if (this._initializingTypeDropdown || !type || type === lesson.lessonTypeID || this._updatingLessonType) {
			return;
		}

		this._updatingLessonType = true;

		this.lessonApiService
			.changeLessonType(lesson.lessonID, +type)
			.pipe(
				take(1),
				catchError((error) => {
					this._updatingLessonType = false;
					if (error.error?.type === 'System.ApplicationException') {
						this.alertService.addAlert({
							type: 'error',
							title: 'Unable to Update Lesson Type',
							message: error.error.message,
							duration: 5000
						} as IAlert);
					} else {
						this.alertService.addAlert({
							type: 'error',
							title: 'Update Failed',
							message: 'Unable to update lesson type. Please try again.',
							duration: 5000
						} as IAlert);
					}
					this.state.loadLesson(lesson.lessonID);
					throw error;
				})
			)
			.subscribe(() => {
				this._updatingLessonType = false;
				this.alertService.addAlert({
					type: 'success',
					title: 'Lesson Type Updated',
					message: 'The lesson type has been successfully updated.',
					duration: 3000
				} as IAlert);
				this.state.reloadAfterChange(lesson.lessonID);
			});
	}

	onJoinLesson(): void {
		this._analytics.trackEvent(AnalyticsEvent.LessonJoined, this._lessonContext());
	}

	private _lessonContext(): Record<string, unknown> {
		const lesson = this.lesson();
		return { lessonId: lesson.lessonID, enrollmentId: lesson.enrollmentID };
	}

	markAbsent(): void {
		const lesson = this.lesson();
		if (!lesson) return;

		const dialogRef = this.dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			icon: 'untitled-ui/general/x-close',
			title: 'Confirm Marking Absent',
			body: 'Are you sure you want to mark this lesson as absent? This will set the attendance status to "Absent" and cannot be undone.',
			successText: 'Mark Absent'
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe((confirmed) => {
			if (confirmed) {
				this.attendanceService.setAttendance(lesson.lessonID, 2, false).subscribe({
					next: () => {
						this._analytics.trackEvent(AnalyticsEvent.LessonMarkedAbsent, this._lessonContext());
						this.state.reloadAfterChange(lesson.lessonID);
					},
					error: () => {
						this.dialogService.open(ConfirmationDialogComponent as Type<Component>, {
							icon: 'untitled-ui/general/x-close',
							title: 'Error',
							body: 'An error occurred while marking the lesson as absent. Please try again.',
							successText: 'Close'
						});
					}
				});
			}
		});
	}

	reschedule(): void {
		this._rescheduleLessonDialogService
			.open(this.lesson())
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					this._analytics.trackEvent(AnalyticsEvent.LessonRescheduled, this._lessonContext());
					this.state.reloadAfterChange(this.lesson().lessonID);
				}
			});
	}

	saveFinalSemester(): void {
		this.formState.markAttemptedSubmit();

		if (!this.formState.isValid()) {
			return;
		}

		const projectGrade = this.formState.projectFinalGrade();
		const projectFinalGradeData = projectGrade
			? {
					gradeName: projectGrade.gradeName,
					gradeColor: (projectGrade.color as IndicatorType) ?? 'default'
				}
			: null;

		const currentAttendanceStatusID = this.lesson().attendanceStatusID ?? null;
		const criteriaGrades = this.formState.projectCriteriaGrades();
		const criteriaItems: ISaveFinalProjectCriteriaItem[] = (this.state.projectCategory()?.criteria ?? []).map((criteria) => {
			const gradeID = criteriaGrades[criteria.criteriaID];
			const grade = this.state.grades().find((g) => g.gradeID === gradeID);

			return {
				criteriaName: criteria.criteriaName,
				gradeName: grade?.gradeName ?? null,
				gradeColor: (grade?.color as IndicatorType) ?? 'default'
			};
		});

		const dialogRef = this.dialogService.open(SaveFinalReviewDialogComponent as Type<Component>, {
			projectGrade: projectFinalGradeData,
			projectCriteriaItems: criteriaItems,
			currentAttendanceStatusID
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe((raw: unknown) => {
			const result = raw as ISaveFinalReviewResult | null;
			if (!result) return;

			const canTakeAttendance = this.canTakeAttendanceForLesson();
			const attendance: IAttendanceSaveParams | undefined =
				canTakeAttendance && result.attendanceStatusID != null
					? { statusID: result.attendanceStatusID, generateInvoice: result.generateInvoice }
					: undefined;

			this.formState.saveFinalSemesterWithAttendance(this.state.lessonID(), attendance);
		});
	}

	/**
	 * Persists whatever the instructor has entered so far without completing the lesson. No validation
	 * (a draft is partial by definition), no review dialog (there is no attendance or invoice to
	 * confirm), and no attendance call.
	 */
	saveDraft(): void {
		if (!this.canSaveDraft()) {
			return;
		}

		this.formState.saveDraft(this.state.lessonID());
	}

	saveLesson(): void {
		this.formState.markAttemptedSubmit();

		if (!this.formState.isValid()) {
			return;
		}

		const categories = this.state.gradingTemplateCategories();
		const grades = this.state.grades();
		const categoryGrades = this.formState.categoryGrades();

		const gradeItems: ISaveReviewGradeItem[] = categories
			.filter((c) => c.categoryType !== GradingTemplateCategoryType.Project)
			.filter((c) => categoryGrades[c.gradingTemplateCategoryID] != null)
			.map((c) => {
				const gradeID = categoryGrades[c.gradingTemplateCategoryID];
				const grade = grades.find((g) => g.gradeID === gradeID);
				return {
					categoryName: c.categoryName,
					gradeName: grade?.gradeName ?? '',
					gradeColor: (grade?.color as IndicatorType) ?? 'default'
				};
			});

		const projectCategory = this.state.projectCategory();
		const milestones = projectCategory?.milestones ?? [];
		const selectedMilestoneID = this.formState.selectedProjectMilestoneID();
		const projectMilestoneName = selectedMilestoneID != null ? milestones.find((m) => m.milestoneID === selectedMilestoneID)?.milestoneName ?? null : null;

		const objectiveIDs = this.formState.selectedObjectives();
		const objectiveOptions = this.state.objectiveOptions();
		const objectiveNames = objectiveIDs.map((id) => {
			const option = objectiveOptions.find((o) => o.value === id);
			return (option?.label as string) ?? `Objective ${id}`;
		});

		const homeworkCategoryIDs = new Set(this.formState.selectedHomeworkCategoryIDs());
		const gradingTemplate = this.state.gradingTemplate();
		// Filtered from the catalog rather than mapped from the selection so tags render in catalog order.
		const homeworkCategories = gradingTemplate
			? gradingTemplate.homeworkCategories.filter((hc) => homeworkCategoryIDs.has(hc.homeworkCategoryID)).map((hc) => hc.categoryName)
			: [];

		// A Homework-type grading category may be explicitly saved as "No Grade" (null). When that
		// happens the category is dropped from gradeItems above, so surface a note in the review dialog.
		const homeworkGradeCategory = categories.find((c) => c.categoryType === GradingTemplateCategoryType.Homework);
		const homeworkNoGrade = homeworkGradeCategory != null && categoryGrades[homeworkGradeCategory.gradingTemplateCategoryID] == null;

		const currentAttendanceStatusID = this.lesson().attendanceStatusID ?? null;

		const dialogRef = this.dialogService.open(SaveReviewDialogComponent as Type<Component>, {
			gradeItems,
			objectiveNames,
			projectMilestoneName,
			homeworkCategories,
			homeworkNoGrade,
			currentAttendanceStatusID,
			canTakeAttendance: this.canTakeAttendanceForLesson(),
			hasDetailChanges: this.formDirty(),
			isMastery: this.state.isMastery(),
			isMultiAttendance: this.isMultiAttendance(),
			presentCount: this.rosterPresentIDs().length,
			absentCount: this.rosterMembers().length - this.rosterPresentIDs().length
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe((raw: unknown) => {
			const result = raw as ISaveReviewResult | null;
			if (!result) return;

			const canTakeAttendance = this.canTakeAttendanceForLesson();
			// Multi-attendance: the roster rides along and the server derives absents + the session
			// value; statusID is ignored by that path. Single: the classic one-value save.
			const attendance: IAttendanceSaveParams | undefined =
				canTakeAttendance && result.attendanceStatusID != null
					? {
							statusID: result.attendanceStatusID,
							generateInvoice: result.generateInvoice,
							presentEnrollmentMemberIDs: this.isMultiAttendance() ? this.rosterPresentIDs() : undefined
						}
					: undefined;

			this.formState.saveStandardLesson(this.state.lessonID(), attendance);
		});
	}
}
